import { Box } from "@mui/material";
import { alpha, lighten } from "@mui/material/styles";
import { ChartData, Point } from "chart.js/auto";
import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../../context/CurrencyContext";
import { useTheme } from "../../../context/ThemeContext";
import { Stage } from "../../../shared/CaseConfiguration";
import {
  SettlementRangeOfStage,
  SettlementRanges,
} from "../../../shared/CaseModel";
import {
  getLegendLabels,
  getTicks,
  getTooltip,
  wrapLabel,
} from "../../../utils/chartjsTheming";
import { intersection } from "./LineIntersection";

/** The height of the chart with its legend, in px. */
const CHART_HEIGHT = 360;

/**
 * How far the two ranges are lightened from the primary colour: the baseline
 * is the lighter one and stands back, the variant is the darker one over it.
 */
const BASELINE_LIGHTEN = 0.6;
const BASELINE_MID_LIGHTEN = 0.4;
const VARIANT_LIGHTEN = 0.2;

/** How much of their colour the areas of the ranges and of the zone take. */
const BASELINE_AREA_ALPHA = 0.35;
const VARIANT_AREA_ALPHA = 0.14;
const ZONE_AREA_ALPHA = 0.34;

/** A line shows a small point per stage, which grows under the pointer. */
const POINTS = {
  pointRadius: 2,
  pointHoverRadius: 6,
  pointHitRadius: 8,
} as const;

function getMinMax(range: SettlementRangeOfStage): {
  min: number;
  max: number;
} {
  if (range.range.partyEv > range.range.opponentEv) {
    return { min: range.range.opponentEv, max: range.range.partyEv };
  }
  return { min: range.range.partyEv, max: range.range.opponentEv };
}

function getRange(
  range1: SettlementRangeOfStage,
  range2: SettlementRangeOfStage,
): { min: number; max: number } {
  const r1 = getMinMax(range1);
  const r2 = getMinMax(range2);
  return { min: Math.max(r1.min, r2.min), max: Math.min(r1.max, r2.max) };
}

export const BargainingLineChart = ({
  rangeOne,
  rangeOneLabel,
  rangeTwo,
  rangeTwoLabel,
  onClick,
}: {
  rangeOne: SettlementRanges;
  rangeOneLabel: string;
  rangeTwo: SettlementRanges;
  rangeTwoLabel: string;
  onClick?: (x: Stage, y: number) => void;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { currency } = useCurrency();

  const labels = rangeOne.perStage.map(({ stage }) =>
    wrapLabel(t("case.stages." + Stage[stage], Stage[stage])),
  );
  const chartData: ChartData<"line", (number | Point)[]> = useMemo(() => {
    const zopa: { min: Point[]; mid: Point[]; max: Point[] } = {
      min: [],
      mid: [],
      max: [],
    };
    let previousWasZopa = false;
    for (
      let i = 0;
      i < Math.min(rangeOne.perStage.length, rangeTwo.perStage.length);
      i++
    ) {
      const element = getRange(rangeOne.perStage[i], rangeTwo.perStage[i]);
      if (element.min < element.max) {
        zopa.min.push({ x: i, y: element.min });
        zopa.mid.push({
          x: i,
          y: element.min + (element.max - element.min) / 2,
        });
        zopa.max.push({ x: i, y: element.max });
        previousWasZopa = true;
      } else {
        if (previousWasZopa) {
          const previousMin = zopa.min[i - 1];
          const previousMax = zopa.max[i - 1];
          const zopaEnd =
            previousMin !== undefined && previousMax !== undefined
              ? intersection(
                  { x1: previousMin.y ?? 0, x2: element.min },
                  { x1: previousMax.y ?? 0, x2: element.max },
                )
              : undefined;
          if (zopaEnd !== undefined) {
            const zopaEndPoint = { y: zopaEnd.x, x: zopaEnd.y + i - 1 };
            zopa.min.push(zopaEndPoint);
            zopa.mid.push(zopaEndPoint);
            zopa.max.push(zopaEndPoint);
          }
        }
        previousWasZopa = false;
      }
    }

    const { main: primary } = theme.palette.primary;
    const { positive } = theme.palette.data;
    const zone = {
      borderColor: positive.main,
      backgroundColor: alpha(positive.main, ZONE_AREA_ALPHA),
      borderWidth: 2,
      ...POINTS,
    };
    const baseline = {
      borderColor: lighten(primary, BASELINE_LIGHTEN),
      backgroundColor: alpha(
        lighten(primary, BASELINE_LIGHTEN),
        BASELINE_AREA_ALPHA,
      ),
      borderWidth: 1.5,
      ...POINTS,
    };
    const variant = {
      borderColor: lighten(primary, VARIANT_LIGHTEN),
      backgroundColor: alpha(primary, VARIANT_AREA_ALPHA),
      borderWidth: 1.5,
      ...POINTS,
    };
    const midOf = (label: string) =>
      t("case.analytics.settlement.bargaining.chart.midOf", { label });

    // the legend names every third of these: the zone and the two ranges
    return {
      datasets: [
        {
          label: t("case.analytics.settlement.bargaining.chart.zoneMaximum"),
          data: zopa.max,
          ...zone,
          order: 1,
        },
        {
          label: t("case.analytics.settlement.bargaining.chart.zoneMinimum"),
          data: zopa.min,
          ...zone,
          order: 1,
          fill: "-1",
        },
        {
          label: rangeOneLabel,
          data: rangeOne.perStage.map(({ range }, index) => ({
            x: index,
            y: range.partyEv,
          })),
          ...baseline,
          order: 2,
        },
        {
          label: rangeOneLabel,
          data: rangeOne.perStage.map(({ range }, index) => ({
            x: index,
            y: range.opponentEv,
          })),
          ...baseline,
          order: 2,
          fill: "-1",
        },
        {
          label: midOf(rangeOneLabel),
          data: rangeOne.perStage.map(({ range }, index) => ({
            x: index,
            y: range.mid,
          })),
          ...baseline,
          borderColor: lighten(primary, BASELINE_MID_LIGHTEN),
          borderDash: [7, 5],
          borderWidth: 2,
          order: 1,
        },
        {
          label: rangeTwoLabel,
          data: rangeTwo.perStage.map(({ range }, index) => ({
            x: index,
            y: range.partyEv,
          })),
          ...variant,
          order: 2,
        },
        {
          label: rangeTwoLabel,
          data: rangeTwo.perStage.map(({ range }, index) => ({
            x: index,
            y: range.opponentEv,
          })),
          ...variant,
          order: 2,
          fill: "-1",
        },
        {
          label: midOf(rangeTwoLabel),
          data: rangeTwo.perStage.map(({ range }, index) => ({
            x: index,
            y: range.mid,
          })),
          ...variant,
          borderColor: primary,
          borderDash: [7, 5],
          borderWidth: 2,
          order: 1,
        },
        {
          label: t("case.analytics.settlement.bargaining.chart.zoneMidpoint"),
          data: zopa.mid,
          ...zone,
          borderColor: positive.dark,
          borderDash: [7, 5],
          order: 0,
        },
      ],
    };
  }, [rangeOne, rangeTwo, rangeOneLabel, rangeTwoLabel, theme, t]);

  return (
    <Box sx={{ height: CHART_HEIGHT, userSelect: "none" }}>
      <Line
        data={chartData}
        options={{
          // the chart fills the frame it is given rather than keeping a shape
          maintainAspectRatio: false,
          onClick: (e, _, chart) => {
            if (!onClick) return;
            if (e.x === null || e.y === null) return;

            // get coordinate values in the scales of chart
            const x = chart.scales.x.getValueForPixel(e.x);
            if (x === undefined) return;
            const y = chart.scales.y.getValueForPixel(e.y);
            if (y === undefined) return;

            // add 10% tolerance for clicking near the stage line
            const stageIndex = Math.floor(x + 0.1);
            const stage = rangeOne.perStage[stageIndex].stage;
            onClick(stage, y);
          },
          interaction: {
            mode: "point",
          },
          plugins: {
            legend: {
              onClick: () => {
                // do nothing
              },
              position: "bottom",
              align: "start",
              labels: {
                ...getLegendLabels(theme),
                boxWidth: 16,
                boxHeight: 10,
                padding: 16,
                filter: ({ datasetIndex }) =>
                  datasetIndex !== undefined && datasetIndex % 3 === 0,
              },
            },
            tooltip: {
              ...getTooltip(theme),
              callbacks: {
                title: () => "",
                label: (item) =>
                  t("charts.tooltip.labelledValue", {
                    label: item.dataset.label,
                    value: currency.format(item.parsed.y ?? 0),
                  }),
              },
            },
          },
          scales: {
            x: {
              type: "linear",
              grid: { display: false },
              ticks: {
                ...getTicks(theme),
                stepSize: 1,
                maxRotation: 0,
                autoSkip: false,
                callback: function (value) {
                  if (typeof value === "number") return labels[value];
                  return value;
                },
              },
            },
            y: {
              beginAtZero: true,
              grid: {
                // the line of zero stands out from the lines of the other amounts
                color: (context) =>
                  context.tick.value === 0
                    ? theme.palette.text.disabled
                    : theme.palette.divider,
              },
              ticks: {
                ...getTicks(theme),
                callback: function (value) {
                  if (typeof value === "number")
                    return currency.formatCompact(value);
                  return value;
                },
              },
            },
          },
        }}
      />
    </Box>
  );
};
